import React, { useState } from "react";
import { Badge, Button, Card, Dropdown, Form, InputGroup, Modal, Row, Col } from "react-bootstrap";
import { LifePart } from "../../state/LifePart";
import { Tracker } from "../../state/Tracker";
import { useData } from "../../storage/accessors";
import TrackerModal from "../growth/trackerModal";
import TrackerCard from "../growth/trackerCard";
import Icon from "../Icon";
import {
    faPlus as add,
    faEdit as edit,
    faTrash as trash,
    faLink,
    faUnlink,
    faChartBar as chartBar
} from '@fortawesome/free-solid-svg-icons';
import { useReadonly } from "@/contexts/readonlyContext";

interface Props {
    taskTracker?: string;
    lifePart: LifePart;
    onTrackerChange: (trackerUuid: string | undefined) => void;
    onCloseModal?: () => void;
    hideEditButton?: boolean;
}

export default function TaskTrackerWidget({ taskTracker, lifePart, onTrackerChange, onCloseModal, hideEditButton = false }: Props) {
    const { trackers, tasks } = useData();
    const { readonly } = useReadonly();
    const [showTrackerModal, setShowTrackerModal] = useState(false);
    const [editingTracker, setEditingTracker] = useState<Tracker | null>(null);
    const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
    const [showTrackerSection, setShowTrackerSection] = useState(!!taskTracker);

    const partTrackers = Object.values(trackers.objects)
        .filter((t) => t.lifepart === lifePart.uuid)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    const linkedTracker = taskTracker ? trackers.objects[taskTracker] : null;

    const handleAddTracker = () => {
        if (readonly) return;
        setEditingTracker(null);
        setShowTrackerModal(true);
    };

    const handleEditTracker = (tracker: Tracker) => {
        if (readonly) return;
        setEditingTracker(tracker);
        setShowTrackerModal(true);
    };

    const handleSaveTracker = (trackerData: Tracker) => {
        if (readonly) return;
        if (editingTracker) {
            trackers.save(editingTracker.uuid, trackerData);
        } else {
            // Создаем новый трекер с новым UUID
            const newTrackerUuid = crypto.randomUUID();
            trackers.create({ ...trackerData, uuid: newTrackerUuid });
            // Автоматически привязываем новый трекер к задаче
            onTrackerChange(newTrackerUuid);
        }
        setShowTrackerModal(false);
        setEditingTracker(null);
    };

    const handleDeleteTracker = (uuid: string) => {
        trackers.delete(uuid);
        setShowTrackerModal(false);
        // Если удалили привязанный трекер, сбрасываем привязку
        if (taskTracker === uuid) {
            onTrackerChange(undefined);
            setShowTrackerSection(false);
        }
    };

    const handleLinkTracker = (trackerUuid: string) => {
        onTrackerChange(trackerUuid);
    };

    const handleUnlinkTracker = () => {
        onTrackerChange(undefined);
        setShowUnlinkConfirm(false);
        setShowTrackerSection(false);
    };

    const handleShowTrackerSection = () => {
        setShowTrackerSection(true);
    };

    // Если трекер привязан, показываем его
    if (linkedTracker) {
        return (
            <>
                <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">
                            <Icon icon={chartBar} className="me-2" />
                            Трекер
                        </h5>
                        <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => setShowUnlinkConfirm(true)}
                            title="Отвязать трекер"
                        >
                            <Icon icon={faUnlink} />
                        </Button>
                    </div>

                    <TrackerCard
                        tracker={linkedTracker}
                        lifePart={lifePart}
                        onEdit={hideEditButton ? undefined : () => handleEditTracker(linkedTracker)}
                    />
                </div>

                {/* Подтверждение отвязки трекера */}
                <Modal show={showUnlinkConfirm} onHide={() => setShowUnlinkConfirm(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Отвязать трекер</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Вы уверены, что хотите отвязать трекер "{linkedTracker?.name}" от этой задачи?</p>
                        <small className="text-muted">
                            Сам трекер и его данные будут сохранены.
                        </small>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => setShowUnlinkConfirm(false)}>
                            Отмена
                        </Button>
                        <Button variant="danger" onClick={handleUnlinkTracker}>
                            Отвязать
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }

    // Если трекер не привязан, показываем кнопку или зону выбора
    if (!showTrackerSection) {
        return (
            <div className="mt-3">
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleShowTrackerSection}
                >
                    <Icon icon={add} className="me-1" />
                    Добавить трекер
                </Button>
            </div>
        );
    }

    // Зона выбора/создания трекера
    return (
        <>
            <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">
                        <Icon icon={chartBar} className="me-2" />
                        Трекер
                    </h5>
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setShowTrackerSection(false)}
                        title="Скрыть"
                    >
                        ×
                    </Button>
                </div>

                <Card className="p-3 bg-body-secondary">
                    <Row className="align-items-center g-2">
                        <Col>
                            <small className="text-muted">
                                Привяжите трекер для отслеживания прогресса выполнения задачи
                            </small>
                        </Col>
                        <Col xs="auto">
                            <Dropdown>
                                <Dropdown.Toggle
                                    variant="outline-primary"
                                    size="sm"
                                    disabled={partTrackers.length === 0}
                                >
                                    <Icon icon={add} className="me-1" />
                                    {partTrackers.length === 0 ? 'Нет трекеров' : 'Выбрать трекер'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {partTrackers.map((tracker) => (
                                        <Dropdown.Item
                                            key={tracker.uuid}
                                            onClick={() => handleLinkTracker(tracker.uuid)}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <span
                                                    className="d-inline-block rounded"
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        backgroundColor: lifePart.color
                                                    }}
                                                />
                                                {tracker.name} ({tracker.measure})
                                            </div>
                                        </Dropdown.Item>
                                    ))}
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={handleAddTracker}>
                                        <Icon icon={add} className="me-2" />
                                        Создать новый трекер
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Col>
                        <Col xs="auto">
                            <Button
                                variant="outline-success"
                                size="sm"
                                onClick={handleAddTracker}
                            >
                                <Icon icon={add} className="me-1" />
                                Новый
                            </Button>
                        </Col>
                    </Row>
                </Card>
            </div>

            {/* Модальное окно для создания/редактирования трекера */}
            <TrackerModal
                key={editingTracker?.uuid ?? 'new'}
                show={showTrackerModal}
                onHide={() => setShowTrackerModal(false)}
                onSave={handleSaveTracker}
                onDelete={handleDeleteTracker}
                tracker={editingTracker}
                lifeParts={[lifePart]}
                defaultLifePart={lifePart.uuid}
            />

            {/* Подтверждение отвязки трекера */}
            <Modal show={showUnlinkConfirm} onHide={() => setShowUnlinkConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Отвязать трекер</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Вы уверены, что хотите отвязать трекер от этой задачи?</p>
                    <small className="text-muted">
                        Сам трекер и его данные будут сохранены.
                    </small>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowUnlinkConfirm(false)}>
                        Отмена
                    </Button>
                    <Button variant="danger" onClick={handleUnlinkTracker}>
                        Отвязать
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}